import { IPaginatedREST } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmAcknowledge, IAlarmEvent, IAlarmGroup, IAlarmSeenBy } from 'plugin-codaf-risk-ext-bowtie/types/alarms'
import {
    BOWTIE_BASE_URL,
    DRA_ALARM_ACKNOWLEDGE,
    DRA_ALARM_COUNT,
    DRA_ALARM_GRAPH,
    DRA_ALARM_GROUPS,
    DRA_ALARM_SEEN_BY,
    DRA_ALARM_STATUS,
    DRA_ALARM_TRIGGER_LOG
} from 'plugin-codaf-risk-ext-bowtie/utils/constants'

export interface IAlarmGroupsFilters {
    perPage: number
    page: number
    keyword: string
    direction?: 'ASC' | 'DESC'
    orderBy?: string
    entityId?: number
}

export interface IAlarmTriggerLogParams {
    limit: number
    offset: number
    startDay: string
    startHour: string
    endDay: string
    endHour: string
    entityId: number
}

export const listAlarmGroupsToList = async (params?: IAlarmGroupsFilters): Promise<IPaginatedREST<IAlarmGroup>> => {
    const qs = new URLSearchParams({
        limit: params?.perPage?.toString() ?? '',
        offset: (Math.max(1, params?.page ?? 0) - 1)?.toString() ?? '0',
        keyword: params?.keyword?.toString() ?? '',
        orderBy: params?.orderBy?.toString()?.toUpperCase() ?? 'GROUPNAME',
        direction: params?.direction?.toString() ?? 'ASC'
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/list/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        return jsonData
    })
}

export const listAlarmGroups = async (params?: IAlarmGroupsFilters): Promise<IPaginatedREST<IAlarmGroup>> => {
    const qs = new URLSearchParams({
        direction: params?.direction ?? 'ASC',
        orderBy: (params?.orderBy ?? 'id').toUpperCase()
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return { ...jsonData, data: jsonData.data.sort((a, b) => a.id - b.id) }
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const updateAlarmGroup = async (body: IAlarmGroup): Promise<IAlarmGroup> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const createAlarmGroup = async (body: IAlarmGroup): Promise<IAlarmGroup> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const deleteAlarmGroup = async (id: number): Promise<void> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const getAlarmGroup = async (id: number): Promise<IAlarmGroup> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GROUPS}/${id}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const getAlarmGraph = async (params?: IAlarmGroupsFilters): Promise<IPaginatedREST<IAlarmEvent>> => {
    const qs = new URLSearchParams({
        direction: params?.direction ?? 'ASC',
        orderBy: (params?.orderBy ?? 'id').toUpperCase(),
        entityId: params.entityId.toString() ?? ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_GRAPH}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

interface ICardData {
    totalFatal: number
    totalWarn: number
    totalError: number
    totalInfo: number
}

export const getAlarmCount = async (params?: IAlarmGroupsFilters): Promise<ICardData> => {
    const qs = new URLSearchParams({
        direction: params?.direction ?? 'ASC',
        orderBy: (params?.orderBy ?? 'id').toUpperCase(),
        entityId: params.entityId.toString() ?? ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_COUNT}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const updateSeenBy = async (body: IAlarmSeenBy): Promise<IAlarmSeenBy> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_STATUS}/${DRA_ALARM_SEEN_BY}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const updateAcknowledge = async (body: IAlarmAcknowledge): Promise<IAlarmAcknowledge> => {
    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_STATUS}/${DRA_ALARM_ACKNOWLEDGE}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}

export const getAlarmTriggerLog = async (params: IAlarmTriggerLogParams): Promise<IPaginatedREST<IAlarmEvent>> => {
    const qs = new URLSearchParams({
        limit: params?.limit.toString() ?? '',
        offset: params?.offset.toString() ?? '',
        startDay: params?.startDay ?? '',
        startHour: params?.startHour ?? '',
        endDay: params?.endDay ?? '',
        endHour: params?.endHour ?? '',
        entityId: params.entityId.toString() ?? ''
    }).toString()

    return fetch(`${BOWTIE_BASE_URL}/${DRA_ALARM_TRIGGER_LOG}/?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
    }).then(async data => {
        let jsonData

        try {
            jsonData = await data.json()
        } catch (err) {
            jsonData = undefined
        }

        if (data.ok) {
            return jsonData
        } else {
            throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message ?? jsonData?.message)
        }
    })
}
